import React, { useState, useCallback, useEffect, ReactNode } from 'react';
import { TokenManager } from '../utils/tokenManager';
import { api } from '../services/authenticatedApi';
import { identifyUser, analytics } from '../services/analytics/posthog';
import { logger } from '../utils/logger';
import {
  JWTAuthContext,
  type AuthError,
  type JWTAuthContextType,
  type SignUpData,
  type SignUpResult,
  type User,
} from './jwtAuthContextInstance';

export type {
  AuthError,
  AuthTokens,
  SignUpData,
  SignUpResult,
  User,
} from './jwtAuthContextInstance';

interface JWTAuthProviderProps {
  children: ReactNode;
}

export const JWTAuthProvider: React.FC<JWTAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<AuthError | null>(null);
  // Track if we've completed initialization to prevent race conditions
  const [isInitialized, setIsInitialized] = useState(false);

  // Handle user state changes - identify in PostHog
  useEffect(() => {
    if (user && !isLoading) {
      identifyUser(user.id, {
        email: user.email,
        firstName: user.firstName,
        company: user.company,
      });
    }
  }, [user, isLoading]);

  // Listen for auth:required events (dispatched by authenticatedApi when tokens are invalid)
  useEffect(() => {
    const handleAuthRequired = () => {
      setUser(null);
      // Don't set isLoading here - state is definitively "not authenticated"
    };

    window.addEventListener('auth:required', handleAuthRequired);
    return () => window.removeEventListener('auth:required', handleAuthRequired);
  }, []);

  // Initialize auth state by checking server session via httpOnly cookies
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Clear any legacy localStorage tokens from before the cookie migration
        TokenManager.clearTokens();

        // Check if the user has a valid session by calling /auth/me
        // The httpOnly access token cookie is sent automatically
        // The api interceptor handles 401 → refresh → retry automatically
        const MAX_RETRIES = 3;
        const BASE_DELAY_MS = 500;

        for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
          try {
            const response = await api.get('/auth/me');
            TokenManager.setCsrfToken?.(response.data.csrfToken);
            setUser(response.data.user);
            setIsLoading(false);
            setIsInitialized(true);
            return;
          } catch (error: unknown) {
            const axiosError = error as { response?: { status?: number } };
            const status = axiosError.response?.status;

            if (status && status >= 400 && status < 500) {
              // Client error (401 after interceptor refresh attempt, 403, etc.) — no valid session
              setUser(null);
              setIsLoading(false);
              setIsInitialized(true);
              return;
            }

            // 5xx or network error — retry with backoff
            if (attempt < MAX_RETRIES) {
              const delay = BASE_DELAY_MS * Math.pow(2, attempt - 1);
              logger.warn(
                `[JWTAuth] Server/network error, retrying in ${delay}ms (attempt ${attempt}/${MAX_RETRIES})`,
                { error },
              );
              await new Promise((resolve) => setTimeout(resolve, delay));
              continue;
            }

            logger.error('[JWTAuth] Server unavailable after retries');
            setUser(null);
            setIsLoading(false);
            setIsInitialized(true);
            return;
          }
        }
      } catch (error) {
        logger.error('[JWTAuth] Auth initialization error', { error });
        setUser(null);
        setIsLoading(false);
        setIsInitialized(true);
      }
    };

    initializeAuth();
  }, []);

  // Error handling
  const handleAuthError = useCallback((error: unknown) => {
    let authError: AuthError;

    const errObj = error as {
      response?: { data?: { code?: string; message?: string; error?: string; field?: string } };
      message?: string;
    };
    if (errObj?.response?.data) {
      const serverError = errObj.response.data;
      authError = {
        code: serverError.code || 'SERVER_ERROR',
        message: serverError.message || serverError.error || 'An error occurred',
        field: serverError.field,
      };
    } else if (errObj?.message) {
      authError = {
        code: 'CLIENT_ERROR',
        message: errObj.message,
      };
    } else {
      authError = {
        code: 'UNKNOWN_ERROR',
        message: 'An unexpected error occurred',
      };
    }

    setError(authError);
    logger.error('Auth error', { authError });
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Sign in - server sets httpOnly cookies on successful login
  const signIn = useCallback(
    async (email: string, password: string, onComplete?: (user: User) => void) => {
      setIsLoading(true);
      clearError();

      try {
        const response = await api.post('/auth/login', { email, password });
        const data = response.data;

        // Server has set httpOnly cookies - no client-side token storage needed
        TokenManager.setCsrfToken?.(data.csrfToken);
        setUser(data.user);
        setIsLoading(false);

        // Clear any lingering demo mode from a previous demo session
        sessionStorage.removeItem('demoMode');
        sessionStorage.removeItem('flowType');

        // Track sign in
        analytics.userLoggedIn('email');

        // Call callback directly after state updates - use setTimeout to let React commit
        if (onComplete && data.user) {
          setTimeout(() => onComplete(data.user), 0);
        }
      } catch (error: unknown) {
        setIsLoading(false);
        handleAuthError(error);
        throw error;
      }
    },
    [handleAuthError, clearError],
  );

  // Sign up - returns SignUpResult to indicate if email verification is required
  const signUp = useCallback(
    async (userData: SignUpData, onComplete?: (user: User) => void): Promise<SignUpResult> => {
      setIsLoading(true);
      clearError();

      try {
        const response = await api.post('/auth/register', userData);
        const data = response.data;

        // Server sets httpOnly cookies if tokens were issued
        if (data.user) {
          TokenManager.setCsrfToken?.(data.csrfToken);
          setUser(data.user);

          // Clear any lingering demo mode from a previous demo session
          sessionStorage.removeItem('demoMode');
          sessionStorage.removeItem('flowType');
        }

        setIsLoading(false);

        // Track sign up
        analytics.userSignedUp('email');

        // Check if email verification is required
        if (data.requiresVerification) {
          // Browser A has no session cookies until verification completes.
          // Stash the verify-pending JWT so /verify-email can present it to
          // the Socket.IO hand-off room and survive a tab refresh.
          if (data.verifyClaim && data.user?.email) {
            try {
              sessionStorage.setItem(
                'verify_session',
                JSON.stringify({
                  email: data.user.email,
                  claim: data.verifyClaim,
                }),
              );
            } catch (storageError) {
              logger.warn('[JWTAuth] Failed to persist verify session', { storageError });
            }
          }

          return {
            requiresVerification: true,
            message: data.message,
            user: data.user,
          };
        }

        // No verification required - call callback
        if (onComplete && data.user) {
          setTimeout(() => onComplete(data.user), 0);
        }

        return {
          requiresVerification: false,
          user: data.user,
        };
      } catch (error: unknown) {
        setIsLoading(false);
        handleAuthError(error);
        throw error;
      }
    },
    [handleAuthError, clearError],
  );

  // Sign out - server clears httpOnly cookies
  const signOut = useCallback(async () => {
    setIsLoading(true);

    try {
      // Revoke refresh token - server clears all auth cookies
      // CSRF token is added automatically by the api interceptor
      try {
        await api.post('/auth/token/revoke');
      } catch (error) {
        // Ignore revocation errors during sign out
        logger.error('Token revocation error', { error });
      }

      // Clear legacy localStorage tokens and user state
      TokenManager.clearTokens();
      setUser(null);

      // Track sign out and reset PostHog
      analytics.userLoggedOut();
    } finally {
      setIsLoading(false);
    }
  }, []);

  const value: JWTAuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    isInitialized,
    signIn,
    signUp,
    signOut,
    error,
    clearError,
  };

  return <JWTAuthContext.Provider value={value}>{children}</JWTAuthContext.Provider>;
};
